import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { denialsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Funnel, Eye, Warning, TrendUp } from '@phosphor-icons/react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

const DENIAL_CATEGORIES = [
  { value: 'documentation_incomplete', label: 'Documentation Incomplete' },
  { value: 'policy_not_active', label: 'Policy Not Active' },
  { value: 'pre_existing_condition', label: 'Pre-existing Condition' },
  { value: 'non_covered_service', label: 'Non-covered Service' },
  { value: 'exceeds_policy_limit', label: 'Exceeds Policy Limit' },
  { value: 'late_submission', label: 'Late Submission' },
  { value: 'incorrect_coding', label: 'Incorrect Coding' },
  { value: 'authorization_not_obtained', label: 'Authorization Not Obtained' },
  { value: 'other', label: 'Other' },
];

const COLORS = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899', '#14B8A6'];

const formatStatus = (status) => {
  return status?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || '';
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(value || 0);
};

const DenialsPage = () => {
  const navigate = useNavigate();
  const [denials, setDenials] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, [statusFilter, categoryFilter]);

  const loadData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      
      const [denialsRes, statsRes] = await Promise.all([
        denialsApi.list(params),
        denialsApi.stats()
      ]);
      setDenials(denialsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load denials:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'recovered': return 'bg-green-100 text-green-800';
      case 'under_appeal': return 'bg-blue-100 text-blue-800';
      case 'written_off': case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-red-100 text-red-800';
    }
  };

  const categoryChartData = stats?.by_category?.map((item, index) => ({
    name: DENIAL_CATEGORIES.find(c => c.value === item.category)?.label || item.category,
    value: item.count,
    amount: item.amount,
    color: COLORS[index % COLORS.length]
  })) || [];

  return (
    <div className="space-y-6" data-testid="denials-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light font-['Outfit'] text-[#1C1C1C]">Denial Management</h1>
          <p className="text-[#595959] mt-1">Track and manage claim denials</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-xs text-[#595959] uppercase">Total Denials</p>
            <p className="text-2xl font-semibold text-[#1C1C1C] font-['Outfit']">{stats?.total_denials || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#F8D7DA] border-0">
          <CardContent className="p-4">
            <p className="text-xs text-[#842029] uppercase">Total Deduction</p>
            <p className="text-2xl font-semibold text-[#842029] font-['Outfit']">{formatCurrency(stats?.total_deduction || 0)}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#D1E7DD] border-0">
          <CardContent className="p-4">
            <p className="text-xs text-[#0F5132] uppercase">Total Recovered</p>
            <p className="text-2xl font-semibold text-[#0F5132] font-['Outfit']">{formatCurrency(stats?.total_recovery || 0)}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#DBEAFE] border-0">
          <CardContent className="p-4">
            <p className="text-xs text-[#1E40AF] uppercase">Under Appeal</p>
            <p className="text-2xl font-semibold text-[#1E40AF] font-['Outfit']">{stats?.by_status?.under_appeal || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      {categoryChartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit'] text-[#1C1C1C]">Denials by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name.substring(0, 15)}... (${(percent * 100).toFixed(0)}%)`}
                    >
                      {categoryChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name, props) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit'] text-[#1C1C1C]">Deduction Amount by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={categoryChartData.slice(0, 5)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                    <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="amount" fill="#EF4444" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 border-[#E5E5E5]">
                <Funnel className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="under_appeal">Under Appeal</SelectItem>
                <SelectItem value="recovered">Recovered</SelectItem>
                <SelectItem value="written_off">Written Off</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-56 border-[#E5E5E5]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {DENIAL_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D4A3E]"></div>
            </div>
          ) : denials.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center p-6">
              <Warning className="h-16 w-16 text-[#A3A3A3] mb-4" weight="light" />
              <p className="text-lg font-medium text-[#1C1C1C]">No denials found</p>
              <p className="text-sm text-[#595959] mt-1">Denials will appear here when recorded</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F5F0] hover:bg-[#F5F5F0]">
                  <TableHead className="font-semibold text-[#1C1C1C]">Case No</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Claim No</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Patient</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Payer</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Category</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Deduction</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Recovered</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Appeals</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Status</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {denials.map((denial) => (
                  <TableRow key={denial.id} className="table-row-hover cursor-pointer" onClick={() => navigate(`/claims/${denial.claim_id}`)}>
                    <TableCell className="font-medium text-[#2D4A3E]">{denial.claim_number}</TableCell>
                    <TableCell className="text-xs font-mono text-orange-600">{denial.insurance_claim_no || '-'}</TableCell>
                    <TableCell>{denial.patient_name}</TableCell>
                    <TableCell>{denial.payer_name}</TableCell>
                    <TableCell>{DENIAL_CATEGORIES.find(c => c.value === denial.category)?.label || denial.category}</TableCell>
                    <TableCell className="text-red-600">{formatCurrency(denial.deduction_amount)}</TableCell>
                    <TableCell className="text-green-600">{formatCurrency(denial.recovery_amount)}</TableCell>
                    <TableCell>{denial.appeal_count}</TableCell>
                    <TableCell>
                      <Badge className={getStatusBadgeClass(denial.status)}>{formatStatus(denial.status)}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/claims/${denial.claim_id}`); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DenialsPage;
